import { Entity } from '../entity';

export interface InboundEmailConfiguration extends Entity {
  _id: string;
  name: string;
  customer: Entity;
  company: Entity;
  fromAddress: string;
  toAddress: string;
  subjectKeywords: string;
  bodyKeywords: string;
  minimumAttachments?: number;
  primaryAttachmentExtension?: AttachmentExtensionType;
  attachmentExtensions?: AttachmentExtensionType[];
  processType: ProcessType
  processHTML?: boolean
  saveAllAttachments?: boolean
  importType: ImportType
}

export enum ProcessType {
  BODY = 'BODY',
  ATTACHMENT = 'ATTACHMENT',
}

export enum AttachmentExtensionType {
  PDF = 'pdf',
  JPG = 'jpg',
  JPEG = 'jpeg',
  PNG = 'png',
  TXT = 'txt',
  DOC = 'doc',
  DOCX = 'docx',
  JSON = 'json',
  XML = 'xml',
  CSV = 'csv',
  XLS = 'xls',
  XLSX = 'xlsx'
}

export enum ImportType {
  ATTACHMENT = 0,

  SPOTLESS_WOTXT = 20, //Spotless

  EMPOWER_WOHTML = 40, //WB-HTML
  EMPOWER_WOTXT = 41, //WB-PDF
  EMPOWER_JSON = 43, //WB-JSON

  PMS_WOTXT2021 = 53, //Programmed-2021
  PMS_AIWOTXT = 54, //AI Programmed

  LAKES_WOXML = 60, //Lakes

  SGCH_WOXML = 80, //SGCH

  BROADSPECTRUM2019 = 102, //Ventia

  EVOLE_WOXML = 130, //Evole

  WENTWORTH_WOPDF = 140, //Wentworth-PDF
  WENTWORTHAI_WOPDF = 141, //Wentworth-PDF

  BRIDGEHOUSING_CSV = 150, //BridgeHousing-CSV
  BRIDGEHOUSING2021_CSV = 151, //BridgeHousing2021-CSV

  HUME_WOPDF = 170, //Hume-PDF
  HUME_WOJSON = 172, //Hume-JSON

  LINKWENTWORTH_WOJSON = 190, //Link-Wentworth-JSON
  LINKWENTWORTH_VARIATION_WOJSON = 191, //Link-Wentworth-Variation-JSON
  LINKWENTWORTH_CANCEL_WOJSON = 192, //Link-Wentworth-Cancel-JSON
  LINKWENTWORTH_NOPREFIX_WOJSON = 193, //Link-Wentworth-NoPrefix-JSON
  LINKWENTWORTH_VARIATION_NOPREFIX_WOJSON = 194, //Link-Wentworth-Variation-NoPrefix-JSON
  LINKWENTWORTH_CANCEL_NOPREFIX_WOJSON = 195, //Link-Wentworth-Cancel-NoPrefix-JSON

  WOMENSHOUSING_CSV = 200, //Womens Housing

  FACILITIESFIRST = 210, //Facilities First

  SRCONSTRUCTION = 220, //SR-Construction
  SR_CONSTRUCTION_AI = 221, //SR-Construction-AI

  RAPID = 250, //Rapid

  DETNSW = 260, //DET-NSW
  DETNSWPrefix = 261, //DET-NSW Prefix
  DETNSW25 = 262, //DET-NSW-25

  PROPERTYME = 270, //PropertyMe

  PROPERTYTREEPDF = 280, //PropertyTree-PDF
  PROPERTYTREE = 281, //PropertyTree-Body

  BRICKSANDAGENT = 290, //Bricks-and-Agent

  STVINCENTDEPAULSH = 300, //StVincent

  DHAHOUSING = 310, //DHA-Housing

  BGIS = 320, //BGIS

  MYPLACEPROPERTYMAINTENANCE = 330, //My Place Property Maintenance

  PACIFICLINKHOUSING = 340, //Pacific Link Housing

  HUNTERPROJECTS = 350, //Hunter Projects
}

export const ImportTypeDisplayNames: Record<ImportType, string> = {
  [ImportType.ATTACHMENT]: 'Attachment',
  [ImportType.SPOTLESS_WOTXT]: 'Spotless',
  [ImportType.EMPOWER_WOTXT]: 'WB PDF',
  [ImportType.EMPOWER_JSON]: 'WB JSON',
  [ImportType.PMS_WOTXT2021]: 'Programmed',
  [ImportType.PMS_AIWOTXT]: 'AI Programmed',
  [ImportType.BROADSPECTRUM2019]: 'Ventia',
  [ImportType.WENTWORTH_WOPDF]: 'Link Wentworth PDF',
  [ImportType.WENTWORTHAI_WOPDF]: 'Link Wentworth PDF AI',
  [ImportType.LINKWENTWORTH_WOJSON]: 'Link Wentworth JSON',
  [ImportType.LINKWENTWORTH_VARIATION_WOJSON]: 'Link Wentworth Variation JSON',
  [ImportType.LINKWENTWORTH_CANCEL_WOJSON]: 'Link Wentworth Cancel JSON',
  [ImportType.LINKWENTWORTH_NOPREFIX_WOJSON]: 'Link Wentworth No Prefix JSON',
  [ImportType.LINKWENTWORTH_VARIATION_NOPREFIX_WOJSON]: 'Link Wentworth Variation No Prefix JSON',
  [ImportType.LINKWENTWORTH_CANCEL_NOPREFIX_WOJSON]: 'Link Wentworth Cancel No Prefix JSON',
  [ImportType.SRCONSTRUCTION]: 'SR Construction',
  [ImportType.SR_CONSTRUCTION_AI]: 'SR Construction AI',
  [ImportType.DETNSW]: 'DET NSW',
  [ImportType.DETNSWPrefix]: 'DET NSW Prefix',
  [ImportType.DETNSW25]: 'DET NSW 2025',
  [ImportType.PROPERTYME]: 'Property Me',
  [ImportType.PROPERTYTREEPDF]: 'PropertyTree PDF',
  [ImportType.PROPERTYTREE]: 'PropertyTree Body',
  [ImportType.BRICKSANDAGENT]: 'Bricks and Agent',
  [ImportType.STVINCENTDEPAULSH]: 'St Vincent',
  [ImportType.DHAHOUSING]: 'DHA Housing',
  [ImportType.EMPOWER_WOHTML]: 'WB HTML',
  [ImportType.LAKES_WOXML]: 'Lakes',
  [ImportType.SGCH_WOXML]: 'SGCH',
  [ImportType.EVOLE_WOXML]: 'Evole',
  [ImportType.BRIDGEHOUSING_CSV]: 'Bridge Housing',
  [ImportType.BRIDGEHOUSING2021_CSV]: 'Bridge Housing 2021',
  [ImportType.HUME_WOPDF]: 'Hume',
  [ImportType.HUME_WOJSON]: 'Hume JSON',
  [ImportType.WOMENSHOUSING_CSV]: 'Womens Housing',
  [ImportType.FACILITIESFIRST]: 'Facilities First',
  [ImportType.RAPID]: 'Rapid',
  [ImportType.BGIS]: 'BGIS',
  [ImportType.MYPLACEPROPERTYMAINTENANCE]: 'My Place Property Maintenance',
  [ImportType.PACIFICLINKHOUSING]: 'Pacific Link Housing',
  [ImportType.HUNTERPROJECTS]: 'Hunter Projects'
};

export function getImportTypeDisplayName(importType: ImportType): string {
  return ImportTypeDisplayNames[importType] || 'Unknown';
}

export function getImportTypeEnumMetadata(): { key: string; value: number }[] {
  return Object.entries(ImportTypeDisplayNames).map(([value, key]) => ({
    key,
    value: Number(value)
  }));
}

export enum IEConfigEntityFormAction {
  PopulateDefaults = 'PopulateIEConfigCommand'
}

export type InboundEmailConfigurationEntityCommandRequest =
  {
    commandId: 'PopulateIEConfigCommand',
    data: {
      importType: ImportType
    }
  };